import { useState } from 'react';
import { Link } from 'react-router-dom';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function BookingPage() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const notes = (form.querySelector('textarea[name="notes"]') as HTMLTextAreaElement)?.value || '';
    if (notes.length > 500) return;

    setFormState('submitting');
    const data = new FormData(form);

    try {
      const res = await fetch('https://formspree.io/f/xbdwjpyz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(data)),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F9F9' }}>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-18 flex items-center justify-between py-4">
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            <img src="/logo.png" alt="Aura Align Physio" className="h-10 w-auto" />
            <span className="text-lg font-bold text-gray-900">Aura Align Physio</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-14">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-5" style={{ backgroundColor: '#E8F5F5', color: '#1A5F5F' }}>
            <i className="ri-calendar-check-line"></i>
            Request an Appointment
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Book Your Session
          </h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Fill in your details below and we&apos;ll reach out to confirm your appointment via your preferred contact method.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <aside className="space-y-5">
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">What to Expect</h3>
              <ul className="space-y-4">
                {[
                  { icon: 'ri-mail-send-line', title: "We'll Contact You", desc: 'Our team reaches out within 1 business day' },
                  { icon: 'ri-time-line', title: 'Flexible Scheduling', desc: 'Evenings & weekends available' },
                  { icon: 'ri-shield-check-line', title: 'AHPRA Registered', desc: 'Fully qualified physiotherapists' },
                  { icon: 'ri-map-pin-line', title: 'Clontarf, QLD', desc: 'Easy access with parking available' },
                ].map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#E8F5F5' }}>
                      <i className={`${item.icon} text-base`} style={{ color: '#1A5F5F' }}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl p-6 text-white" style={{ backgroundColor: '#1A5F5F' }}>
              <h3 className="font-semibold mb-3">Need help?</h3>
              <p className="text-sm text-teal-100 mb-4">Prefer to call us directly? We&apos;re happy to chat.</p>
              <a href="tel:0731425833" className="flex items-center gap-2 text-sm font-medium text-white hover:text-teal-200 transition-colors cursor-pointer">
                <i className="ri-phone-line"></i>
                07 3142 5833
              </a>
              <a href="tel:0485683288" className="flex items-center gap-2 text-sm font-medium text-white hover:text-teal-200 transition-colors cursor-pointer mt-2">
                <i className="ri-phone-line"></i>
                0485 683 288
              </a>
            </div>
          </aside>

          <div className="lg:col-span-2">
            {formState === 'success' ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E8F5F5' }}>
                  <i className="ri-check-line text-4xl" style={{ color: '#1A5F5F' }}></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Request Received!
                </h2>
                <p className="text-gray-500 mb-8 max-w-sm mx-auto">
                  Thanks for reaching out. We&apos;ll get back to you within 1 business day to confirm your appointment.
                </p>
                <Link to="/" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-white transition-all hover:scale-105 cursor-pointer" style={{ backgroundColor: '#1A5F5F' }}>
                  <i className="ri-home-line"></i>
                  Back to Home
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 p-8 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="firstName">First Name <span className="text-red-400">*</span></label>
                    <input id="firstName" name="firstName" type="text" required placeholder="e.g. Sarah" className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="lastName">Last Name <span className="text-red-400">*</span></label>
                    <input id="lastName" name="lastName" type="text" required placeholder="e.g. Johnson" className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="email">Email Address <span className="text-red-400">*</span></label>
                    <input id="email" name="email" type="email" required placeholder="you@example.com" className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="phone">Phone Number <span className="text-red-400">*</span></label>
                    <input id="phone" name="phone" type="tel" required placeholder="04xx xxx xxx" className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method <span className="text-red-400">*</span></label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: 'email', label: 'Email', icon: 'ri-mail-line' },
                      { value: 'phone', label: 'Phone Call', icon: 'ri-phone-line' },
                      { value: 'text', label: 'Text / SMS', icon: 'ri-message-2-line' },
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-200 cursor-pointer hover:border-teal-500 transition-colors has-[:checked]:border-teal-500 has-[:checked]:bg-teal-50 text-sm font-medium text-gray-700">
                        <input type="radio" name="contactMethod" value={opt.value} required className="accent-teal-600" />
                        <i className={`${opt.icon} text-base`} style={{ color: '#1A5F5F' }}></i>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="preferredDate">Preferred Date <span className="text-red-400">*</span></label>
                    <input id="preferredDate" name="preferredDate" type="date" required min={new Date().toISOString().split('T')[0]} className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="preferredTime">Preferred Time</label>
                    <select id="preferredTime" name="preferredTime" className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors">
                      <option value="">No preference</option>
                      <option value="morning">Morning (8am – 12pm)</option>
                      <option value="afternoon">Afternoon (12pm – 5pm)</option>
                      <option value="evening">Evening (5pm – 9pm)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="notes">
                    Additional Notes <span className="text-gray-400 font-normal ml-1">(optional)</span>
                  </label>
                  <textarea id="notes" name="notes" rows={4} maxLength={500} placeholder="Tell us what type of service you need, your condition, or any questions you have..." onChange={(e) => setCharCount(e.target.value.length)} className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors resize-none" />
                  <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>{charCount}/500</p>
                </div>

                {formState === 'error' && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm">
                    <i className="ri-error-warning-line text-lg"></i>
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}

                <button type="submit" disabled={formState === 'submitting'} className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-white text-base transition-all hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap" style={{ backgroundColor: '#1A5F5F' }}>
                  {formState === 'submitting' ? (
                    <><i className="ri-loader-4-line animate-spin text-lg"></i>Sending Request...</>
                  ) : (
                    <><i className="ri-send-plane-line text-lg"></i>Send Booking Request</>
                  )}
                </button>
                <p className="text-xs text-center text-gray-400">By submitting this form you agree to be contacted by our team regarding your appointment request.</p>
              </form>
            )}
          </div>
        </div>
      </main>

      <footer className="mt-16 border-t border-gray-100 py-6 text-center text-sm text-gray-400">
        &copy; {new Date().getFullYear()} Aura Align Physio · Clontarf, QLD
      </footer>
    </div>
  );
}
